import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import DragHandleVerticalIcon from '@atlaskit/icon/core/migration/drag-handle-vertical--drag-handler';
import InlineEdit from '@atlaskit/inline-edit';
import { Flex, xcss } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages.tsx';

export type TitleEditorProps = {
	isRenamable: boolean;
	isRenaming: boolean;
	title: string;
	children: ReactNode;
	onConfirmEdit: (arg1: string) => void;
	onCancelEdit: () => void;
	onEdit?: () => void;
	editButtonLabel?: string;
};

export const TitleEditor = ({
	isRenamable,
	isRenaming,
	title,
	onConfirmEdit,
	onCancelEdit,
	onEdit,
	editButtonLabel,
	children,
}: TitleEditorProps) => {
	const { formatMessage } = useIntl();

	return !isRenamable ? (
		<>{children}</>
	) : (
		<Flex>
			{!isRenaming && (
				<DragHandlerWrapper>
					<DragHandleVerticalIcon
						spacing="spacious"
						label={formatMessage(messages.dragLabel)}
						color={token('color.text')}
					/>
				</DragHandlerWrapper>
			)}
			<InlineEditWrapper data-testid="dashboard-edit.ui.gadget-editable.title-editor.inline-edit-wrapper">
				<InlineEdit
					label=""
					defaultValue={title}
					isEditing={isRenaming}
					editView={({ errorMessage, ...fieldProps }) => (
						<Textfield
							{...fieldProps}
							aria-label="Edit gadget title text input"
							testId="dashboard-edit.ui.gadget-editable.title-editor.text-input"
						/>
					)}
					readView={() => children}
					onConfirm={onConfirmEdit}
					onCancel={onCancelEdit}
					readViewFitContainerWidth
					onEdit={onEdit}
					editButtonLabel={editButtonLabel}
				/>
			</InlineEditWrapper>
		</Flex>
	);
};

const DragHandlerWrapper = ({ children }: { children: ReactNode }) => (
	<Flex xcss={dragHandlerWrapperStyles}>{children}</Flex>
);

const dragHandlerWrapperStyles = xcss({
	flexShrink: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InlineEditWrapper = styled.div({
	width: '100%',
	cursor: 'text',
	// Inline edit form styling
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& form': {
		// Form styling has max width so that inline edit's read view text has hidden overflow
		maxWidth: '100%',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& form > div:nth-of-type(1)': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		marginTop: `${token('space.0')} !important`,
	},
});
